import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import { clampQuantity, readStorage, writeStorage } from "../utils/store";

export const CartContext = createContext(null);
const matches = (item, id, size, color) =>
  item.id === Number(id) && item.size === size && item.color === color;
const validItems = (items) =>
  Array.isArray(items)
    ? items
        .filter((item) => {
          if (!item || typeof item !== "object") return false;
          const product = productData.find((p) => p.id === item.id);
          return (
            product &&
            product.available !== false &&
            product.sizes?.includes(item.size) &&
            product.colors?.includes(item.color)
          );
        })
        .map((item) => ({
          id: item.id,
          size: item.size,
          color: item.color,
          quantity: clampQuantity(item.quantity),
        }))
    : [];

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState(() =>
    validItems(readStorage(`${siteConfig.storageKey}:cart`, [])),
  );
  const [wishlist, setWishlist] = useState(() => {
    const saved = readStorage(`${siteConfig.storageKey}:wishlist`, []);
    return Array.isArray(saved)
      ? [...new Set(saved.filter((id) => productData.some((p) => p.id === id)))]
      : [];
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [storageError, setStorageError] = useState(false);
  useEffect(() => {
    setStorageError(!writeStorage(`${siteConfig.storageKey}:cart`, cartItems));
  }, [cartItems]);
  useEffect(() => {
    writeStorage(`${siteConfig.storageKey}:wishlist`, wishlist);
  }, [wishlist]);

  const addToCart = useCallback((productId, quantity = 1, size, color) => {
    const product = productData.find((p) => p.id === Number(productId));
    if (
      !product ||
      product.available === false ||
      !product.sizes.includes(size) ||
      !product.colors.includes(color)
    )
      return false;
    setCartItems((items) => {
      const existing = items.find((item) =>
        matches(item, productId, size, color),
      );
      return existing
        ? items.map((item) =>
            matches(item, productId, size, color)
              ? {
                  ...item,
                  quantity: clampQuantity(
                    item.quantity + clampQuantity(quantity),
                  ),
                }
              : item,
          )
        : [
            ...items,
            { id: product.id, size, color, quantity: clampQuantity(quantity) },
          ];
    });
    setCartOpen(true);
    return true;
  }, []);
  const removeFromCart = useCallback(
    (id, size, color) =>
      setCartItems((items) =>
        items.filter((item) => !matches(item, id, size, color)),
      ),
    [],
  );
  const updateQuantity = useCallback(
    (id, size, color, quantity) =>
      setCartItems((items) =>
        items.map((item) =>
          matches(item, id, size, color)
            ? { ...item, quantity: clampQuantity(quantity) }
            : item,
        ),
      ),
    [],
  );
  const clearCart = useCallback(() => setCartItems([]), []);
  const addWishlist = useCallback((id) => {
    if (productData.some((p) => p.id === id))
      setWishlist((items) => (items.includes(id) ? items : [...items, id]));
  }, []);
  const removeWishlist = useCallback(
    (id) => setWishlist((items) => items.filter((item) => item !== id)),
    [],
  );
  const lines = useMemo(
    () =>
      cartItems.map((item) => ({
        ...item,
        product: productData.find((p) => p.id === item.id),
      })),
    [cartItems],
  );
  const cartCount = cartItems.reduce((count, item) => count + item.quantity, 0);
  const subtotal = lines.reduce(
    (sum, item) => sum + item.product.price * item.quantity,
    0,
  );
  const value = {
    cartItems,
    lines,
    cartCount,
    subtotal,
    addToCart,
    addCart: addToCart,
    removeFromCart,
    removeCart: removeFromCart,
    updateQuantity,
    clearCart,
    wishlist,
    addWishlist,
    removeWishlist,
    cartOpen,
    setCartOpen,
    storageError,
  };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
